import type { ComponentType } from 'react'
import type { WebSocketHookReturn } from '../../hooks/useWebSocket'
import type { Book } from '../../types'

/** Props every drawer panel adapter receives from ModuleDrawer. */
export interface DrawerPanelProps {
  ws: WebSocketHookReturn
  books: Book[]
  research?: Book
  /** The `:id` of the current `/book/:id` route, when on one. */
  accountId?: number
}

/**
 * One lazily-loaded block of a drawer. `load` is a dynamic import of a thin
 * default-export adapter in `./panels/<Name>Panel` that maps
 * DrawerPanelProps onto an existing component's props.
 */
export interface DrawerSection {
  heading?: string
  load: () => Promise<{ default: ComponentType<DrawerPanelProps> }>
}

export interface DrawerEntry {
  title: string
  subtitle?: string
  sections: DrawerSection[]
}

export type DrawerRoute = 'fleet' | 'book' | 'ops'

const section = (
  heading: string,
  load: DrawerSection['load']
): DrawerSection => ({ heading, load })

const FleetValueOverlay = section('Fleet value overlay', () => import('./panels/FleetValueOverlayPanel'))
const BookValue = section('Book value', () => import('./panels/BookValueChartsPanel'))
const Picks = section('Picks vs SPY', () => import('./panels/PicksPanelAdapter'))
const Scorecard = section('Scorecard', () => import('./panels/ScorecardTablePanel'))
const RiskBackstop = section('Risk backstop', () => import('./panels/RiskBackstopPanel'))
const Overlay = section('Overlay', () => import('./panels/OverlayCardPanel'))
const Holdings = section('Holdings', () => import('./panels/HoldingsPanelAdapter'))
// AccountPanel reads the bare ['account'] / ['positions'] keys: the default
// (active-pointer) book, i.e. the research book. Labelled as such, and kept
// out of fleet-scoped drawers like P&L (review I4).
const ResearchAccount = section('Research book · default account', () => import('./panels/AccountPanelAdapter'))
const Services = section('Services', () => import('./panels/ServicesStatusPanel'))
const CloudWatch = section('CloudWatch metrics', () => import('./panels/CloudWatchMetricsPanel'))
const WorkflowRuns = section('Workflow runs', () => import('./panels/WorkflowRunsFeedPanel'))
const BookCards = section('Books', () => import('./panels/BookCardsPanel'))
const Conviction = section('Conviction', () => import('./panels/ConvictionStripPanel'))
const Funnel = section('Lifecycle funnel', () => import('./panels/LifecycleFunnelPanel'))
const IncubationRoster = section('Incubation roster', () => import('./panels/IncubationRosterPanelAdapter'))
const Sector = section('Sector mix', () => import('./panels/SectorPanelAdapter'))
const Executions = section('Recent executions', () => import('./panels/RecentExecutionsPanel'))
const CapRejections = section('Cap rejections', () => import('./panels/CapRejectionsFeedPanel'))

// Book-route sections. Every one resolves its book from the URL's account id
// (BookRequired), never from the research book: keys such as `sector`,
// `scorecard`, `exposure` and `drawdown-floor` recur across routes and books.
// Headings are asserted by the reachability matrix in drawerRegistry.test.ts.
const BookHeader = section('Book', () => import('./panels/BookCardPanel'))
const BookPicks = section('Picks vs SPY', () => import('./panels/PicksPanelAdapter'))
const BookScorecard = section('Scorecard', () => import('./panels/BookScorecardPanel'))
const BookMirror = section('Mirror fidelity', () => import('./panels/BookMirrorFidelityPanel'))
const BookFeed = section('Subscriber feed', () => import('./panels/BookSubscriberFeedPanel'))
const BookContainment = section('Containment', () => import('./panels/BookDormancyPanel'))
const BookKillEntries = section('Kill-entries', () => import('./panels/BookKillEntriesPanel'))
const BookBackstop = section('Risk backstop', () => import('./panels/BookRiskBackstopPanel'))
const BookOverlay = section('Overlay', () => import('./panels/BookOverlayPanel'))
const BookHoldings = section('Holdings', () => import('./panels/BookHoldingsPanel'))
const BookValve = section('Valve', () => import('./panels/BookValvePanel'))
const BookTax = section('Tax summary', () => import('./panels/BookTaxPanel'))
const BookValueSection = section('Book value', () => import('./panels/BookValueChartPanel'))
const BookSector = section('Sector mix', () => import('./panels/BookSectorPanel'))
const BookBench = section('Bench', () => import('./panels/BookBenchPanel'))
const BookCohorts = section('Cohorts', () => import('./panels/BenchCohortsPanel'))
const BookCadence = section('Research cadence', () => import('./panels/BookCadencePanel'))
const BookDossier = section('Dossier A/B', () => import('./panels/BookDossierABPanel'))
const BookInception = section('Clean inception', () => import('./panels/BookCleanInceptionPanel'))
const BookCapRejectsNote = section('About this feed', () => import('./panels/CapRejectsNotePanel'))
const BookCapRejections = section('Live-cap rejections', () => import('./panels/BookCapRejectionsPanel'))

// Ops-route sections. Platform-wide components, mounted as the legacy OpsPage
// mounted them (ws only where it passed ws). Headings are asserted by the
// reachability matrix in drawerRegistry.test.ts.
const AgentGrid = section('Agent health grid', () => import('./panels/AgentHealthGridPanel'))
const Agent24h = section('Agent health · 24h', () => import('./panels/AgentHealth24hPanel'))
const VetoRates = section('Rule veto rates', () => import('./panels/VetoRateTrendsPanel'))
const SignalPipelineSection = section('Signal pipeline', () => import('./panels/SignalPipelinePanel'))
const SanityFeed = section('Sanity gate feed', () => import('./panels/LiveSanityGateFeedPanel'))
const Topology = section('Topology', () => import('./panels/TopologyDiagramPanel'))
const RiskMonitorSection = section('Risk monitor', () => import('./panels/RiskMonitorPanel'))
const MLEnsemble = section('ML ensemble', () => import('./panels/MLEnsembleStatusPanel'))
const Goals = section('Goals', () => import('./panels/GoalsProgressPanel'))
const PlatformTax = section('Platform tax', () => import('./panels/PlatformTaxPanel'))
const LLMBacktest = section('LLM backtest', () => import('./panels/LLMBacktestZonePanel'))
const AdaptiveTuning = section('Adaptive tuning', () => import('./panels/AdaptiveTuningZonePanel'))
const StopEngine = section('Stop engine', () => import('./panels/StopEnginePanel'))
const LLMCadence = section('LLM cadence', () => import('./panels/LLMCadenceTogglePanel'))
const LLMCost = section('LLM reasoning · cost', () => import('./panels/LLMCostTilePanel'))
const LLMEnvelope = section('LLM reasoning · envelope', () => import('./panels/LLMEnvelopeViewPanel'))
const LLMAudit = section('LLM reasoning · audit', () => import('./panels/LLMAuditListPanel'))
const LLMPrecedents = section('LLM reasoning · precedents', () => import('./panels/LLMPrecedentListPanel'))

/**
 * Route-scoped `?panel=<key>` registry. Keys only resolve on the route they
 * are registered under. Entries are filled in by later tasks.
 */
export const DRAWER_REGISTRY: Record<DrawerRoute, Record<string, DrawerEntry>> = {
  fleet: {
    'fleet-equity': {
      title: 'Fleet equity',
      subtitle: '3 books · broker-backed',
      sections: [FleetValueOverlay, BookValue],
    },
    'fleet-pnl': {
      title: 'P&L today',
      subtitle: 'flow-netted, per book',
      sections: [BookValue],
    },
    'picks-excess': {
      title: 'Picks vs SPY',
      subtitle: 'research book',
      sections: [Picks, Scorecard],
    },
    'drawdown-floor': {
      title: 'Drawdown & floor',
      subtitle: 'wargame 22 middle-path risk',
      sections: [RiskBackstop, Overlay],
    },
    exposure: {
      title: 'Exposure',
      subtitle: 'research book · gross / equity',
      sections: [Holdings],
    },
    'engine-alarms': {
      title: 'Engine & alarms',
      sections: [ResearchAccount, Services, CloudWatch, WorkflowRuns],
    },
    'fleet-value': {
      title: 'Fleet value',
      subtitle: 'indexed to 100 at range start',
      sections: [FleetValueOverlay],
    },
    'picks-horizons': {
      title: 'Picks vs SPY by horizon',
      subtitle: 'research book',
      sections: [Picks],
    },
    books: {
      title: 'Books',
      subtitle: 'research · validation · live',
      sections: [BookCards],
    },
    pipeline: {
      title: 'Incubation pipeline',
      subtitle: 'research book',
      sections: [Conviction, Funnel, IncubationRoster],
    },
    sector: {
      title: 'Sector mix',
      subtitle: 'research book · shed bar from config',
      sections: [Sector],
    },
    activity: {
      title: "Today's activity",
      subtitle: 'research book (default) — broker-backed, not per-book',
      sections: [Executions, WorkflowRuns, BookCapRejectsNote, CapRejections],
    },
  },
  book: {
    'book-header': {
      title: 'Book',
      subtitle: 'registry row · engine status',
      sections: [BookHeader],
    },
    'picks-excess': {
      title: 'Picks vs SPY',
      subtitle: 'research book',
      sections: [BookPicks, BookScorecard],
    },
    'mirror-fidelity': {
      title: 'Mirror fidelity',
      subtitle: 'validation book · mirrors the research HELD set',
      sections: [BookMirror, BookFeed],
    },
    'live-gate': {
      title: 'Live gate',
      subtitle: 'live book · double-gated',
      sections: [BookContainment, BookKillEntries],
    },
    'drawdown-floor': {
      title: 'Drawdown & floor',
      subtitle: 'this book · wargame 22 middle-path risk',
      sections: [BookBackstop, BookOverlay],
    },
    exposure: {
      title: 'Exposure',
      subtitle: 'this book · gross / equity',
      sections: [BookHoldings],
    },
    // Ruling 34: the valve, then the book's own scorecard, so the live book's
    // ScorecardTable stays reachable although its page has no scorecard module.
    'exit-valve': {
      title: 'Thesis-break valve',
      subtitle: 'research valve · mirrored by validation and live',
      sections: [BookValve, BookScorecard],
    },
    tax: {
      title: 'Tax',
      subtitle: 'this book · per-book lot ledger',
      sections: [BookTax],
    },
    'book-value': {
      title: 'Book value',
      subtitle: 'this book · equity history',
      sections: [BookValueSection, BookBackstop],
    },
    'holdings-heat': {
      title: 'Holdings',
      subtitle: 'this book · broker positions',
      sections: [BookHoldings],
    },
    scorecard: {
      title: 'Scorecard',
      subtitle: 'this book',
      sections: [BookScorecard],
    },
    sector: {
      title: 'Sector mix',
      subtitle: 'this book · shed bar from config',
      sections: [BookSector],
    },
    bench: {
      title: 'Bench',
      subtitle: 'research book',
      sections: [BookBench, BookCohorts, IncubationRoster],
    },
    cadence: {
      title: 'Research cadence',
      subtitle: 'derived client-side · holiday-blind',
      sections: [BookCadence, BookDossier],
    },
    overlay: {
      title: 'Config overlay',
      subtitle: 'this book · changes activate next cycle',
      sections: [BookOverlay],
    },
    mirror: {
      title: 'Mirror',
      subtitle: 'validation book',
      sections: [BookMirror, BookFeed],
    },
    inception: {
      title: 'Clean inception',
      subtitle: 'validation book · permanent canary',
      sections: [BookInception, BookOverlay],
    },
    'kill-entries': {
      title: 'Kill-entries',
      subtitle: 'live book · restrict-only',
      sections: [BookKillEntries],
    },
    // Ruling 36: the note comes FIRST. The feed's own empty state ("No
    // live-cap rejections.") would otherwise read as an all-clear.
    'cap-rejects': {
      title: 'Live-cap rejections',
      subtitle: 'live book · limits not shown by design',
      sections: [BookCapRejectsNote, BookCapRejections],
    },
    dormancy: {
      title: 'Containment',
      subtitle: 'live book · double gate',
      sections: [BookContainment, BookOverlay],
    },
  },
  ops: {
    engine: {
      title: 'Engine',
      subtitle: 'engine-wide',
      sections: [WorkflowRuns, ResearchAccount],
    },
    'runs-24h': {
      title: 'Workflow runs',
      subtitle: 'since engine start on the face',
      sections: [WorkflowRuns],
    },
    services: {
      title: 'Services',
      sections: [Services],
    },
    cloudwatch: {
      title: 'CloudWatch metrics',
      subtitle: 'alarm states not yet surfaced',
      sections: [CloudWatch],
    },
    agents: {
      title: 'Agents',
      sections: [AgentGrid, VetoRates],
    },
    market: {
      title: 'Market',
      subtitle: 'system health',
      sections: [ResearchAccount, SignalPipelineSection],
    },
    'agent-health': {
      title: 'Agent health',
      subtitle: '24h',
      sections: [AgentGrid, Agent24h, VetoRates],
    },
    'signal-pipeline': {
      title: 'Signal pipeline',
      subtitle: 'default book',
      sections: [SignalPipelineSection, SanityFeed, Topology],
    },
    'sanity-gate': {
      title: 'Sanity gate',
      subtitle: 'since page load',
      sections: [SanityFeed],
    },
    'risk-monitor': {
      title: 'Risk monitor',
      subtitle: 'default book · controls live here',
      sections: [RiskMonitorSection],
    },
    models: {
      title: 'Models',
      sections: [MLEnsemble, VetoRates],
    },
    goals: {
      title: 'Goals',
      subtitle: 'platform tax moved here',
      sections: [Goals, PlatformTax],
    },
    // Ruling 46: every control lives here (AdaptiveTuningZone owns the
    // external-API pause), then the four retired-LLM-reasoning panels.
    operator: {
      title: 'Operator',
      subtitle: 'guarded controls',
      sections: [LLMBacktest, AdaptiveTuning, StopEngine, LLMCadence, LLMCost, LLMEnvelope, LLMAudit, LLMPrecedents],
    },
  },
}

/** '/ops' → ops, '/book/:id' → book, everything else → fleet. */
export function routeFor(pathname: string): DrawerRoute {
  if (pathname === '/ops') return 'ops'
  if (pathname.startsWith('/book/')) return 'book'
  return 'fleet'
}
